import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './AdminWebRoles.css';

/**
 * Admin: WebRole-hantering — Sprint 3 av Fluxer-migrationen.
 *
 * Voice-platform-agnostiska roller (Sprint 1). En roll kan tilldelas
 * Players och kopplas till bot-kommandon (CommandPermission). Här
 * hanterar admin själva rollerna och deras medlemmar — koppling till
 * kommandon görs i AdminPermissions (samexisterar med Discord-roller).
 *
 * Behörighet: 'admin.permissions' eller guild-owner.
 */
export default function AdminWebRoles({ perms, hideTitle = false }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Create-form state
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // ?guild_id=... så backend väljer rätt guild (annars Guild.objects.first()
  // och WebRoles på "fel" guild blir osynliga).
  const guildParam = perms?.guildId ? `?guild_id=${perms.guildId}` : '';

  const fetchRoles = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/web-roles${guildParam}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setRoles((await r.json()).roles || []);
      } else {
        setError(t('admin.loadRolesFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, guildParam, t]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const fetchMembers = useCallback(async (roleId) => {
    if (!roleId) {
      setMembers([]);
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/admin/web-roles/${roleId}/members${guildParam}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setMembers((await r.json()).members || []);
      } else {
        setMembers([]);
      }
    } catch {
      setMembers([]);
    }
  }, [getAuthHeaders, guildParam]);

  useEffect(() => {
    fetchMembers(selectedRoleId);
  }, [selectedRoleId, fetchMembers]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/web-roles${guildParam}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), description: newDescription.trim() }),
      });
      if (r.ok) {
        setNewName('');
        setNewDescription('');
        fetchRoles();
      } else {
        const data = await r.json();
        setError(data.error === 'duplicate_name'
          ? t('admin.duplicateRoleName')
          : data.error || t('admin.createRoleFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(t('admin.confirmDeleteRole', { name: role.name }))) {
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/admin/web-roles/${role.id}${guildParam}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        if (selectedRoleId === role.id) setSelectedRoleId(null);
        fetchRoles();
      } else {
        setError(t('admin.deleteRoleFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    }
  };

  const handleSearchPlayers = async (query) => {
    setMemberSearch(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      // Återanvänd access-list-endpoint för spelar-sökning
      const guildQs = perms?.guildId ? `&guild_id=${perms.guildId}` : '';
      const r = await fetch(
        `${API_URL}/api/admin/access?status=all&q=${encodeURIComponent(query)}&limit=20${guildQs}`,
        { headers: getAuthHeaders() },
      );
      if (r.ok) {
        setSearchResults((await r.json()).players || []);
      }
    } catch {
      setSearchResults([]);
    }
  };

  const handleAddMember = async (player) => {
    if (!selectedRoleId) return;
    try {
      const r = await fetch(
        `${API_URL}/api/admin/web-roles/${selectedRoleId}/members${guildParam}`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ player_id: player.id }),
        },
      );
      if (r.ok) {
        setMemberSearch('');
        setSearchResults([]);
        fetchMembers(selectedRoleId);
        fetchRoles();  // member_count uppdateras
      } else {
        setError(t('admin.addMemberFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedRoleId) return;
    if (!window.confirm(t('admin.confirmRemoveMember'))) return;
    try {
      const r = await fetch(
        `${API_URL}/api/admin/web-roles/${selectedRoleId}/members/${memberId}${guildParam}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (r.ok) {
        fetchMembers(selectedRoleId);
        fetchRoles();
      } else {
        setError(t('admin.removeMemberFailed'));
      }
    } catch {
      setError(t('settings.networkError'));
    }
  };

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  return (
    <div className="admin-web-roles">
      {!hideTitle && <h1 className="admin-section-title">{t('admin.subWebRoles')}</h1>}
      <p className="admin-section-help">
        {t('admin.webRolesHelp')}
      </p>

      {error && <div className="admin-error">{error}</div>}

      <div className="webroles-grid">
        <div className="admin-card webroles-list">
          <div className="admin-card-title">{t('admin.rolesTitle')}</div>
          {loading ? (
            <div className="admin-info">{t('live.loading')}</div>
          ) : (
            <div className="webroles-items">
              {roles.map((r) => (
                <button
                  key={r.id}
                  className={`webroles-item${selectedRoleId === r.id ? ' webroles-item-active' : ''}`}
                  onClick={() => setSelectedRoleId(r.id)}
                  type="button"
                >
                  <span className="webroles-item-name">{r.name}</span>
                  <span className="webroles-item-meta">
                    {r.member_count} {r.member_count === 1 ? t('admin.memberWordOne') : t('admin.memberWordMany')}
                    {r.commands.length > 0 && ` · ${r.commands.length} ${t('admin.commandsWord')}`}
                  </span>
                </button>
              ))}
              {roles.length === 0 && (
                <div className="admin-info">{t('admin.noRolesCreated')}</div>
              )}
            </div>
          )}

          <div className="webroles-create">
            <div className="webroles-create-title">{t('admin.createNewRole')}</div>
            <input
              className="admin-input"
              type="text"
              placeholder={t('admin.roleNamePlaceholder')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={100}
            />
            <input
              className="admin-input"
              type="text"
              placeholder={t('admin.descriptionPlaceholder')}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              maxLength={255}
            />
            <button
              className="admin-btn"
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              type="button"
            >
              {creating ? t('admin.creating') : t('admin.create')}
            </button>
          </div>
        </div>

        <div className="admin-card webroles-detail">
          {!selectedRole ? (
            <div className="admin-info">{t('admin.selectRoleLeft')}</div>
          ) : (
            <>
              <div className="webroles-detail-header">
                <div>
                  <div className="admin-card-title">{selectedRole.name}</div>
                  {selectedRole.description && (
                    <div className="webroles-description">{selectedRole.description}</div>
                  )}
                </div>
                <button
                  className="admin-btn admin-btn-danger"
                  onClick={() => handleDelete(selectedRole)}
                  type="button"
                >
                  {t('admin.deleteRole')}
                </button>
              </div>

              {selectedRole.commands.length > 0 && (
                <div className="webroles-commands">
                  <span className="webroles-commands-label">{t('admin.linkedCommands')}</span>
                  {selectedRole.commands.map((cmd) => (
                    <span key={cmd} className="webroles-command-pill">{cmd}</span>
                  ))}
                </div>
              )}

              <div className="webroles-members-title">
                {t('admin.membersCount', { count: members.length })}
              </div>
              <div className="webroles-add-member">
                <input
                  className="admin-input"
                  type="text"
                  placeholder={t('admin.searchPlayerToAdd')}
                  value={memberSearch}
                  onChange={(e) => handleSearchPlayers(e.target.value)}
                />
                {searchResults.length > 0 && (
                  <div className="webroles-search-results">
                    {searchResults.map((p) => (
                      <button
                        key={p.id}
                        className="webroles-search-item"
                        onClick={() => handleAddMember(p)}
                        type="button"
                      >
                        <span>{p.display_name}</span>
                        <span className="webroles-search-meta">
                          {p.access_status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {members.length === 0 ? (
                <div className="admin-info">{t('admin.noMembersYet')}</div>
              ) : (
                <div className="webroles-members">
                  {members.map((m) => (
                    <div key={m.id} className="webroles-member">
                      {m.steam_avatar && (
                        <img src={m.steam_avatar} alt="" className="webroles-member-avatar" />
                      )}
                      <div className="webroles-member-info">
                        <div className="webroles-member-name">{m.display_name}</div>
                        <div className="webroles-member-meta">
                          {m.steam_username && <span>Steam: {m.steam_username}</span>}
                          {m.discord_username && <span>Discord: {m.discord_username}</span>}
                        </div>
                      </div>
                      <button
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleRemoveMember(m.id)}
                        type="button"
                      >
                        {t('admin.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
